/* Automatische Lösungen zu Prüfergebnissen (werden im Prüf-Dialog von Hand ausgelöst): überflüssige Brushes löschen,
 * Brushes zu func_detail machen. Ohne DOM. */
import type { Brush, CheckFix, Doc, Entity, Vec3 } from './types.ts';
import Cmd from './cmds.ts';
import { coveredFaces, hiddenBrushes } from './perf.ts';
import { HINT, SKIP, distortedFaces, isHintMixed, zFightOverlaps } from './lint.ts';
import { alignedAxes, faceNormal, rebuildBrush } from './brush.ts';
import { newId } from './ids.ts';
import { COMPILER_FGDS, FGD_KEY, addFgdName } from './modprofile.ts';
import { validBrush, volume } from './simplify.ts';
import { parseText } from '../formats/map-parse.ts';
import { brushText } from '../formats/map-write.ts';

/** Gültig auch nach Schreiben und Einlesen als .map (der Compiler sieht nur die drei Punkte je Ebene): die Eckpunkte werden dort aus den Ebenen neu berechnet */
const survivesText = (b: Brush): boolean => {
  const r = parseText('{\n"classname" "worldspawn"\n' + brushText({ ...b, srcText: undefined }) + '\n}\n');
  const nb = r.doc && r.doc.ents[0] && r.doc.ents[0].brushes[0];
  return !!nb && validBrush(nb) && Math.abs(volume(nb) - volume(b)) <= Math.max(1, 0.02 * volume(b));
};

/** Konvexe, gültige und textbeständige Form aus den Eckpunkten eines kaputten Brushes: Eckpunkte aufs Raster, Hülle bilden;
 *  ist sie nicht textbeständig (nahe beieinander liegende Ebenen), fällt der Eckpunkt mit dem kleinsten Volumenbeitrag weg, bis es passt. */
export function convexRepair(b: Brush): Brush | null {
  const snap = (v: Vec3): Vec3 => [Math.round(v[0]), Math.round(v[1]), Math.round(v[2])];
  const tries: Vec3[][] = [b.verts, b.verts.map(snap)];
  for (const pts of tries) { const nb = rebuildBrush(b, pts); if (nb && validBrush(nb) && survivesText(nb)) return nb; }
  let pts = b.verts.map(snap);
  for (let round = 0; round < 40 && pts.length > 4; round++) {
    const full = rebuildBrush(b, pts, { tol: 1e-6 });
    if (!full) return null;
    if (validBrush(full) && survivesText(full)) return full;
    let best = -1, bestV = Infinity;
    for (let i = 0; i < full.verts.length; i++) {
      const rest = rebuildBrush(b, full.verts.filter((_, k) => k !== i), { tol: 1e-6 });
      const v = rest ? volume(rest) : -1;
      if (rest && v >= 0 && volume(full) - v < bestV) { bestV = volume(full) - v; best = i; }
    }
    if (best < 0) return null;
    pts = full.verts.filter((_, k) => k !== best);
  }
  return null;
}

const hasLevel = (e: Entity): boolean => { const n = parseInt(e.kv.zhlt_detaillevel || '', 10); return Number.isFinite(n) && n >= 1; };

/** Wendet die Lösung auf die genannten Brushes an. count = Zahl der geänderten Brushes (0: nichts zu tun) */
export function applyFix(doc: Doc, fix: CheckFix, target: string[] | { b: string[]; e: string[] }): { doc: Doc; count: number } {
  const ids = Array.isArray(target) ? target : target.b, set = new Set(ids), eset = new Set(Array.isArray(target) ? [] : target.e);
  if (fix === 'delete') {
    const sel = Cmd.emptySel(); let count = 0;
    doc.ents.forEach((e, i) => {
      e.brushes.forEach((b) => { if (set.has(b.id)) { sel.b.add(b.id); count++; } });
      if (i > 0 && !e.brushes.length && eset.has(e.id)) { sel.e.add(e.id); count++; }
    });
    return count ? { doc: Cmd.remove(doc, sel).doc, count } : { doc, count: 0 };
  }
  if (fix === 'null') {
    const cov = coveredFaces(doc); let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => set.has(b.id) && cov.has(b.id))
      ? { ...e, brushes: e.brushes.map((b) => { const l = set.has(b.id) ? cov.get(b.id) : undefined; if (!l) return b; count += l.length; return { ...b, srcText: undefined, faces: b.faces.map((f, fi) => (l.includes(fi) ? { ...f, tex: 'NULL' } : f)) }; }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'snap') {
    let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => set.has(b.id))
      ? { ...e, brushes: e.brushes.map((b) => {
          if (!set.has(b.id)) return b;
          const nb = rebuildBrush(b, b.verts.map((v) => [Math.round(v[0]), Math.round(v[1]), Math.round(v[2])] as [number, number, number]));
          if (!nb) return b;
          count++; return nb;
        }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'fgd') {
    // Mod-Profil der Karte: FGD der Compiler-Werkzeuge hinten anfügen (spätere Dateien gewinnen, die Spiel-FGD bleibt vorn)
    const w = doc.ents[0];
    if (!w) return { doc, count: 0 };
    const next = addFgdName(w.kv[FGD_KEY], COMPILER_FGDS[0]);
    if (next === (w.kv[FGD_KEY] || '')) return { doc, count: 0 };
    return { doc: { ents: doc.ents.map((e, i) => (i === 0 ? { ...e, kv: { ...e.kv, [FGD_KEY]: next } } : e)) }, count: 1 };
  }
  if (fix === 'zfight') {
    // Je Überlagerung bleibt eine Fläche sichtbar: die vollständig verdeckte wird NULL, sonst übernimmt die eine das Aussehen der anderen
    // (gleiche Textur und Ausrichtung flackert nicht sichtbar). Die Fläche eines Brush-Entity weicht der Welt, damit sie beim Bewegen die Weltfläche freigibt.
    const order = new Map<string, number>(), world = new Set<string>(), where = new Map<string, Brush>();
    doc.ents.forEach((e, ei) => e.brushes.forEach((b) => { order.set(b.id, order.size); if (ei === 0) world.add(b.id); where.set(b.id, b); }));
    const nulled = new Set<string>(), patch = new Map<string, Partial<Brush['faces'][number]>>();
    const key = (id: string, fi: number) => id + ':' + fi;
    const look = (f: Brush['faces'][number]) => ({ tex: f.tex, ua: f.ua, uo: f.uo, va: f.va, vo: f.vo, rot: f.rot, xs: f.xs, ys: f.ys });
    const faceOf = (k: string) => { const [id, fi] = k.split(':'); return (where.get(id) as Brush).faces[+fi]; };
    const rank = (id: string) => (world.has(id) ? 0 : 1) * 1e6 + (order.get(id) as number);
    // Gruppen über alle Überlagerungen (auch bei gleichem Aussehen), bearbeitet werden nur Gruppen, in denen etwas flackert
    const all = zFightOverlaps(doc, new Set(hiddenBrushes(doc)), true).filter((o) =>
      faceOf(key(o.a, o.fa)).tex.charAt(0) !== '{' && faceOf(key(o.b, o.fb)).tex.charAt(0) !== '{');
    const parent = new Map<string, string>();
    const find = (k: string): string => { let r = k; while (parent.get(r) !== r) r = parent.get(r) as string; parent.set(k, r); return r; };
    const same = (o: { a: string; b: string; fa: number; fb: number }) => JSON.stringify(look(faceOf(key(o.a, o.fa)))) === JSON.stringify(look(faceOf(key(o.b, o.fb))));
    for (const o of all) {
      const ka = key(o.a, o.fa), kb = key(o.b, o.fb);
      [ka, kb].forEach((k) => { if (!parent.has(k)) parent.set(k, k); });
      const ra = find(ka), rb = find(kb);
      if (ra !== rb) parent.set(ra, rb);
    }
    const bad = new Set<string>();
    all.forEach((o) => { if (!same(o) && (set.has(o.a) || set.has(o.b))) bad.add(find(key(o.a, o.fa))); });   // Gruppen, die einen gewählten Brush berühren
    const pairs = all.filter((o) => bad.has(find(key(o.a, o.fa))));
    // 1) Vollständig verdeckte Fläche wird NULL (bei zwei gleich großen die des Entity bzw. des später definierten Brushes; eine Weltfläche nie hinter einem Entity)
    for (const o of pairs) {
      const ka = key(o.a, o.fa), kb = key(o.b, o.fb);
      if (nulled.has(ka) || nulled.has(kb)) continue;
      let loser: 'a' | 'b' | null = null;
      if (o.coverA && o.coverB) loser = rank(o.a) > rank(o.b) ? 'a' : 'b';
      else if (o.coverA) loser = 'a'; else if (o.coverB) loser = 'b';
      if (!loser) continue;
      const lid = loser === 'a' ? o.a : o.b, wid = loser === 'a' ? o.b : o.a;
      if (world.has(lid) && !world.has(wid)) continue;
      nulled.add(key(lid, loser === 'a' ? o.fa : o.fb));
    }
    // 2) Teilweise Überlagerungen: zusammenhängende Flächen bekommen alle das Aussehen der ersten (Welt vor Entity, dann Definitionsreihenfolge)
    const groups = new Map<string, string[]>();
    parent.forEach((_p, k) => { if (nulled.has(k) || !bad.has(find(k))) return; const r = find(k); (groups.get(r) || groups.set(r, []).get(r) as string[]).push(k); });
    groups.forEach((ks) => {
      ks.sort((x, y) => rank(x.split(':')[0]) - rank(y.split(':')[0]) || +x.split(':')[1] - +y.split(':')[1]);
      const keep = look(faceOf(ks[0]));
      ks.slice(1).forEach((k) => patch.set(k, keep));
    });
    let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => b.faces.some((_f, i) => nulled.has(key(b.id, i)) || patch.has(key(b.id, i))))
      ? { ...e, brushes: e.brushes.map((b) => {
          let hit = false;
          const faces = b.faces.map((f, i) => {
            if (nulled.has(key(b.id, i))) { hit = true; return { ...f, tex: 'NULL' }; }
            const pt = patch.get(key(b.id, i));
            if (pt) { hit = true; return { ...f, ...pt }; }
            return f;
          });
          if (!hit) return b;
          count++; return { ...b, srcText: undefined, faces };
        }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'texalign') {
    // Verzerrte Flächen: Texturachsen in die Flächenebene legen (senkrecht zueinander), Skalierung und Versatz bleiben
    let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => set.has(b.id))
      ? { ...e, brushes: e.brushes.map((b) => {
          if (!set.has(b.id)) return b;
          const bad = new Set(distortedFaces(b));
          if (!bad.size) return b;
          count += bad.size;
          return { ...b, srcText: undefined, faces: b.faces.map((f, i) => (bad.has(i) ? { ...f, ...alignedAxes(faceNormal(b.verts, f.vi), f.ua, f.va) } : f)) };
        }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'hull') {
    // Ungültige Brushes: konvexe Hülle der Eckpunkte ( Flächen werden eben, Einbuchtungen gefüllt); Texturen der nächstliegenden alten Fläche
    let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => set.has(b.id))
      ? { ...e, brushes: e.brushes.map((b) => {
          if (!set.has(b.id) || validBrush(b)) return b;
          const nb = convexRepair(b);
          if (!nb) return b;
          count++; return nb;
        }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'skip') {
    // Hint-Brushes: HINT-Flächen bleiben, alle anderen Flächen werden SKIP (gemischte Inhalte bricht csg ab)
    let count = 0;
    const ents = doc.ents.map((e) => (e.brushes.some((b) => set.has(b.id))
      ? { ...e, brushes: e.brushes.map((b) => {
          if (!set.has(b.id) || !isHintMixed(b)) return b;
          count++; return { ...b, srcText: undefined, faces: b.faces.map((f) => (HINT.test(f.tex) || SKIP.test(f.tex) ? f : { ...f, tex: 'SKIP' })) };
        }) }
      : e));
    return count ? { doc: { ents }, count } : { doc, count: 0 };
  }
  if (fix === 'noclip') {
    // Die gewählten Brushes in func_detail-Entities bekommen zhlt_noclip 1: ganze Entity -> Schlüssel setzen, sonst die Brushes in eine neue func_detail gleicher Stufe auslagern
    let count = 0; const extra: Entity[] = [];
    const ents = doc.ents.map((e, i) => {
      if (i === 0 || e.kv.classname !== 'func_detail' || e.kv.zhlt_noclip === '1') return e;
      const pick = e.brushes.filter((b) => set.has(b.id));
      if (!pick.length) return e;
      count += pick.length;
      if (pick.length === e.brushes.length) return { ...e, kv: { ...e.kv, zhlt_noclip: '1' } };
      extra.push({ id: newId(), kv: { ...e.kv, zhlt_noclip: '1' }, brushes: pick });
      return { ...e, brushes: e.brushes.filter((b) => !set.has(b.id)) };
    });
    return count ? { doc: { ents: ents.concat(extra) }, count } : { doc, count: 0 };
  }
  // 'detail': Welt-Brushes wandern in ein neues func_detail mit zhlt_detaillevel 1; vorhandene func_detail ohne Stufe bekommen sie
  let count = 0;
  const moved = doc.ents[0] ? doc.ents[0].brushes.filter((b) => set.has(b.id)) : [];
  const ents = doc.ents.map((e, i) => {
    if (i === 0) return moved.length ? { ...e, brushes: e.brushes.filter((b) => !set.has(b.id)) } : e;
    if (e.kv.classname === 'func_detail' && !hasLevel(e) && e.brushes.some((b) => set.has(b.id))) {
      count += e.brushes.length;
      return { ...e, kv: { ...e.kv, zhlt_detaillevel: '1' } };
    }
    return e;
  });
  if (moved.length) { ents.push({ id: newId(), kv: { classname: 'func_detail', zhlt_detaillevel: '1' }, brushes: moved }); count += moved.length; }
  return count ? { doc: { ents }, count } : { doc, count: 0 };
}
